import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog, PageLoader, EmptyState, ActionBtn, SectionHeader, FormField, StatCard, Pagination } from '../components/common/UI';
import { toShamsi, formatCurrency } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import { MdAdd, MdEdit, MdDelete, MdVisibility, MdPrint } from 'react-icons/md';
import { FaBoxes, FaMoneyBillWave, FaCheckCircle, FaTools, FaUser, FaUserSecret } from 'react-icons/fa';
import ShamsiDatePicker from '../components/common/ShamsiDatePicker';

const initialForm = {
  name: '', category: '', quantity: 1, rentalPrice: '', condition: '', status: 'موجود', location: '', notes: ''
};

export default function RentalEquipment() {
  const { isManager } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalValue, setTotalValue] = useState(0);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    fullName: '', fatherName: '', phone: '', email: '', idCardNumber: '', address: '', photo: ''
  });
  const [guarantorForm, setGuarantorForm] = useState({
    fullName: '', fatherName: '', phone: '', email: '', idCardNumber: '', photo: '', relationship: ''
  });
  const [rentalDates, setRentalDates] = useState({ startDate: '', endDate: '' });
  const [deposit, setDeposit] = useState(0);

  const fetchItems = useCallback(async () => {
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (filterStatus) params.status = filterStatus;
      
      const res = await API.get('/rental-equipment', { params });
      setItems(res.data.data);
      setTotalValue(res.data.total);
    } catch (err) {
      toast.error('خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterStatus]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const filtered = items.filter(i =>
    !search || i.name?.includes(search) || i.location?.includes(search) || i.customer?.fullName?.includes(search)
  );

  // پیج‌بندی (صفحه‌بندی سمت کلاینت)
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search, filterCategory, filterStatus]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openAdd = () => { 
    setForm(initialForm); 
    setEditItem(null); 
    setCustomerForm({ fullName: '', fatherName: '', phone: '', email: '', idCardNumber: '', address: '', photo: '' });
    setGuarantorForm({ fullName: '', fatherName: '', phone: '', email: '', idCardNumber: '', photo: '', relationship: '' });
    setRentalDates({ startDate: '', endDate: '' });
    setDeposit(0);
    setShowModal(true); 
  };
  
  const openEdit = (item) => {
    setForm({
      name: item.name || '', category: item.category || '', quantity: item.quantity || 1,
      rentalPrice: item.rentalPrice || '', condition: item.condition || '', status: item.status || 'موجود',
      location: item.location || '', notes: item.notes || ''
    });
    setCustomerForm(item.customer || { fullName: '', fatherName: '', phone: '', email: '', idCardNumber: '', address: '', photo: '' });
    setGuarantorForm(item.guarantor || { fullName: '', fatherName: '', phone: '', email: '', idCardNumber: '', photo: '', relationship: '' });
    setRentalDates({ startDate: item.rentalStartDate?.split('T')[0] || '', endDate: item.rentalEndDate?.split('T')[0] || '' });
    setDeposit(item.deposit || 0);
    setEditItem(item);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.rentalPrice) {
      return toast.error('نام تجهیز و قیمت ضروری است');
    }
    
    if (form.status === 'کرایه شده' && (!customerForm.fullName || !customerForm.phone)) {
      return toast.error('برای وضعیت کرایه شده، اطلاعات مشتری ضروری است');
    }
    
    setSaving(true);
    const dataToSend = {
      name: form.name,
      category: form.category,
      quantity: Number(form.quantity),
      rentalPrice: Number(form.rentalPrice),
      condition: form.condition,
      status: form.status,
      location: form.location,
      notes: form.notes,
      deposit: Number(deposit),
      customer: {
        fullName: customerForm.fullName,
        fatherName: customerForm.fatherName,
        phone: customerForm.phone,
        email: customerForm.email,
        idCardNumber: customerForm.idCardNumber,
        address: customerForm.address
      },
      guarantor: {
        fullName: guarantorForm.fullName,
        fatherName: guarantorForm.fatherName,
        phone: guarantorForm.phone,
        email: guarantorForm.email,
        idCardNumber: guarantorForm.idCardNumber,
        relationship: guarantorForm.relationship
      },
      rentalStartDate: rentalDates.startDate,
      rentalEndDate: rentalDates.endDate
    };
    
    try {
      if (editItem) {
        await API.put(`/rental-equipment/${editItem._id}`, dataToSend);
        toast.success('تجهیز بروزرسانی شد');
      } else {
        await API.post('/rental-equipment', dataToSend);
        toast.success('تجهیز اضافه شد');
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطا');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/rental-equipment/${deleteId}`);
      toast.success('تجهیز حذف شد');
      setDeleteId(null);
      fetchItems();
    } catch (err) {
      toast.error('خطا در حذف');
    }
  };

  const handlePrintRental = () => {
    const printContent = document.getElementById('rental-print-content');
    if (!printContent) {
      toast.error('خطا در چاپ');
      return;
    }
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const getStatusColor = (status) => {
    if (status === 'موجود') return 'bg-green-100 text-green-700 border border-green-200';
    if (status === 'کرایه شده') return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
    if (status === 'تعمیر') return 'bg-red-100 text-red-700 border border-red-200';
    return 'bg-gray-500/20 text-gray-600';
  };

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader
        title="تجهیزات کرایی"
        subtitle={`${filtered.length} تجهیز | ارزش کل: ${formatCurrency(totalValue)}`}
        actions={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="تجهیز جدید" />}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={<FaBoxes size={18} className="text-blue-600" />} label="کل تجهیزات" value={items.length} color="blue" />
        <StatCard icon={<FaCheckCircle size={18} className="text-green-600" />} label="موجود" value={items.filter(i => i.status === 'موجود').length} color="green" />
        <StatCard icon={<FaMoneyBillWave size={18} className="text-purple-600" />} label="ارزش کل" value={formatCurrency(totalValue)} color="purple" />
        <StatCard icon={<FaTools size={18} className="text-red-600" />} label="در تعمیر" value={items.filter(i => i.status === 'تعمیر').length} color="red" />
      </div>

      <div className="glass-card p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input type="text" className="form-input" placeholder="جستجو..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <input type="text" className="form-input sm:w-40" placeholder="دسته بندی" value={filterCategory} onChange={e => setFilterCategory(e.target.value)} />
          <input type="text" className="form-input sm:w-40" placeholder="وضعیت" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="تجهیزی یافت نشد" subtitle="اولین تجهیز کرایی را اضافه کنید" />
      ) : (
        <>
          <div className="glass-card overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="premium-table">
                <thead>
                  <tr>
                    <th>نام</th><th>دسته</th><th>تعداد</th><th>قیمت روزانه</th><th>وضعیت</th><th>مشتری</th><th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map(item => (
                    <tr key={item._id}>
                      <td className="font-semibold text-gray-800">{item.name}{item.condition && <span className="text-xs text-gray-600 mr-1">({item.condition})</span>}</td>
                      <td className="text-gray-600">{item.category || '---'}</td>
                      <td className="text-gray-600">{item.quantity}</td>
                      <td className="text-green-600">{formatCurrency(item.rentalPrice)}</td>
                      <td><span className={`badge ${getStatusColor(item.status)}`}>{item.status}</span></td>
                      <td className="text-gray-500 text-sm">{item.customer?.fullName || '---'}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setViewItem(item)} className="p-1.5 text-blue-600 hover:text-blue-700 rounded-lg"><MdVisibility size={16} /></button>
                          {isManager && <button onClick={() => openEdit(item)} className="p-1.5 text-yellow-600 hover:text-yellow-700 rounded-lg"><MdEdit size={16} /></button>}
                          {isManager && <button onClick={() => setDeleteId(item._id)} className="p-1.5 text-red-600 hover:text-red-700 rounded-lg"><MdDelete size={16} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="md:hidden space-y-3">
            {paged.map(item => (
              <div key={item._id} className="mobile-card">
                <div className="flex justify-between items-start mb-2">
                  <div><div className="font-semibold text-gray-800">{item.name}</div><div className="text-xs text-gray-500">{item.category || '---'}</div></div>
                  <span className={`badge ${getStatusColor(item.status)}`}>{item.status}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                  <div><span className="text-gray-600">تعداد:</span> {item.quantity}</div>
                  <div><span className="text-gray-600">قیمت:</span> {formatCurrency(item.rentalPrice)}</div>
                  {item.customer?.fullName && <div className="col-span-2"><span className="text-gray-600">مشتری:</span> {item.customer.fullName}</div>}
                </div>
                {isManager && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => setViewItem(item)} className="flex-1 py-1.5 text-xs bg-blue-100 text-blue-600 rounded-lg">جزئیات</button>
                    <button onClick={() => openEdit(item)} className="flex-1 py-1.5 text-xs bg-gray-50 text-gray-800 rounded-lg">ویرایش</button>
                    <button onClick={() => setDeleteId(item._id)} className="flex-1 py-1.5 text-xs bg-red-100 text-red-600 rounded-lg">حذف</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      {/* مودال اضافه/ویرایش */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? 'ویرایش کرایه' : 'کرایه جدید'} size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><h3 className="text-gray-800 font-bold mb-2">اطلاعات تجهیز</h3></div>
          
          <FormField label="نام تجهیز" required>
            <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </FormField>
          <FormField label="دسته بندی">
            <input className="form-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} />
          </FormField>
          <FormField label="تعداد">
            <input type="number" className="form-input" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} />
          </FormField>
          <FormField label="قیمت کرایه روزانه" required>
            <input type="number" className="form-input" value={form.rentalPrice} onChange={e => setForm({ ...form, rentalPrice: e.target.value })} />
          </FormField>
          <FormField label="پیش پرداخت (بیمه)">
            <input type="number" className="form-input" value={deposit} onChange={e => setDeposit(e.target.value)} />
          </FormField>
          <FormField label="وضعیت فیزیکی">
            <input className="form-input" value={form.condition} onChange={e => setForm({ ...form, condition: e.target.value })} placeholder="نو، خوب، متوسط، کهنه" />
          </FormField>
          <FormField label="وضعیت کرایه">
            <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="موجود">موجود</option>
              <option value="کرایه شده">کرایه شده</option>
              <option value="تعمیر">تعمیر</option>
            </select>
          </FormField>
          <FormField label="لوکیشن">
            <input className="form-input" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
          </FormField>
          
          <FormField label="تاریخ شروع کرایه">
            <ShamsiDatePicker value={rentalDates.startDate} onChange={date => setRentalDates({...rentalDates, startDate: date})} className="form-input" />
          </FormField>
          <FormField label="تاریخ پایان کرایه">
            <ShamsiDatePicker value={rentalDates.endDate} onChange={date => setRentalDates({...rentalDates, endDate: date})} className="form-input" />
          </FormField>
          
          <div className="sm:col-span-2"><h3 className="text-gray-800 font-bold mb-2 mt-2">اطلاعات مشتری</h3></div>
          
          <FormField label="نام کامل" required>
            <input className="form-input" value={customerForm.fullName} onChange={e => setCustomerForm({...customerForm, fullName: e.target.value})} />
          </FormField>
          <FormField label="نام پدر">
            <input className="form-input" value={customerForm.fatherName} onChange={e => setCustomerForm({...customerForm, fatherName: e.target.value})} />
          </FormField>
          <FormField label="شماره تلفن" required>
            <input className="form-input" value={customerForm.phone} onChange={e => setCustomerForm({...customerForm, phone: e.target.value})} />
          </FormField>
          <FormField label="ایمیل">
            <input type="email" className="form-input" value={customerForm.email} onChange={e => setCustomerForm({...customerForm, email: e.target.value})} />
          </FormField>
          <FormField label="شماره تذکره">
            <input className="form-input" value={customerForm.idCardNumber} onChange={e => setCustomerForm({...customerForm, idCardNumber: e.target.value})} />
          </FormField>
          <FormField label="آدرس">
            <input className="form-input" value={customerForm.address} onChange={e => setCustomerForm({...customerForm, address: e.target.value})} />
          </FormField>
          
          <div className="sm:col-span-2"><h3 className="text-gray-800 font-bold mb-2 mt-2">اطلاعات ضامن (اختیاری)</h3></div>
          
          <FormField label="نام کامل ضامن">
            <input className="form-input" value={guarantorForm.fullName} onChange={e => setGuarantorForm({...guarantorForm, fullName: e.target.value})} />
          </FormField>
          <FormField label="نام پدر ضامن">
            <input className="form-input" value={guarantorForm.fatherName} onChange={e => setGuarantorForm({...guarantorForm, fatherName: e.target.value})} />
          </FormField>
          <FormField label="شماره تلفن ضامن">
            <input className="form-input" value={guarantorForm.phone} onChange={e => setGuarantorForm({...guarantorForm, phone: e.target.value})} />
          </FormField>
          <FormField label="نسبت با مشتری">
            <input className="form-input" value={guarantorForm.relationship} onChange={e => setGuarantorForm({...guarantorForm, relationship: e.target.value})} placeholder="مثال: برادر، پدر، دوست" />
          </FormField>
          <FormField label="شماره تذکره ضامن">
            <input className="form-input" value={guarantorForm.idCardNumber} onChange={e => setGuarantorForm({...guarantorForm, idCardNumber: e.target.value})} />
          </FormField>
          
          <div className="sm:col-span-2">
            <FormField label="توضیحات">
              <textarea className="form-input h-20 resize-none" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </FormField>
          </div>
        </div>
        
        <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
          <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800">انصراف</button>
          <ActionBtn onClick={handleSave} label={saving ? 'ذخیره...' : editItem ? 'بروزرسانی' : 'ذخیره'} disabled={saving} />
        </div>
      </Modal>

{/* مودال جزئیات */}
{/* مودال جزئیات */}
{viewItem && (
  <Modal isOpen={!!viewItem} onClose={() => setViewItem(null)} title="جزئیات کامل کرایه" size="lg">
    <div className="space-y-5">
      {/* محتوای اصلی برای نمایش */}
      <div className="space-y-5 max-h-[60vh] overflow-y-auto">
        {/* هدر */}
        <div className="flex justify-between items-center border-b pb-3">
          <h2 className="text-xl font-bold text-gray-800">{viewItem.name}</h2>
          <span className={`badge ${getStatusColor(viewItem.status)}`}>{viewItem.status}</span>
        </div>

        {/* اطلاعات تجهیز */}
        <div>
          <div className="text-xs text-gray-600 mb-2 flex items-center gap-2 border-r-2 border-blue-500 pr-2">
            <FaBoxes className="text-blue-600" /> اطلاعات تجهیز
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-gray-50 rounded-lg">
            <div><span className="text-gray-600">نام تجهیز:</span> <span className="text-gray-800">{viewItem.name}</span></div>
            <div><span className="text-gray-600">دسته بندی:</span> <span className="text-gray-800">{viewItem.category || '—'}</span></div>
            <div><span className="text-gray-600">تعداد:</span> <span className="text-gray-800">{viewItem.quantity}</span></div>
            <div><span className="text-gray-600">قیمت روزانه:</span> <span className="text-green-600">{formatCurrency(viewItem.rentalPrice)}</span></div>
            <div><span className="text-gray-600">پیش پرداخت:</span> <span className="text-yellow-600">{formatCurrency(viewItem.deposit || 0)}</span></div>
            <div><span className="text-gray-600">وضعیت فیزیکی:</span> <span className="text-gray-800">{viewItem.condition || '—'}</span></div>
            <div><span className="text-gray-600">لوکیشن:</span> <span className="text-gray-800">{viewItem.location || '—'}</span></div>
            <div><span className="text-gray-600">تاریخ ایجاد:</span> <span className="text-gray-800">{toShamsi(viewItem.createdAt)}</span></div>
          </div>
        </div>

        {/* تاریخ کرایه */}
        {(viewItem.rentalStartDate || viewItem.rentalEndDate) && (
          <div>
            <div className="text-xs text-gray-600 mb-2 flex items-center gap-2 border-r-2 border-purple-500 pr-2">
              <FaMoneyBillWave className="text-purple-600" /> تاریخ کرایه
            </div>
            <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg">
              <div><span className="text-gray-600">تاریخ شروع:</span> <span className="text-gray-800">{viewItem.rentalStartDate ? toShamsi(viewItem.rentalStartDate) : '—'}</span></div>
              <div><span className="text-gray-600">تاریخ پایان:</span> <span className="text-gray-800">{viewItem.rentalEndDate ? toShamsi(viewItem.rentalEndDate) : '—'}</span></div>
            </div>
          </div>
        )}

        {/* اطلاعات مشتری */}
        {viewItem.customer && (
          <div>
            <div className="text-xs text-gray-600 mb-2 flex items-center gap-2 border-r-2 border-green-500 pr-2">
              <FaUser className="text-green-600" /> اطلاعات مشتری
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-gray-50 rounded-lg">
              <div><span className="text-gray-600">نام کامل:</span> <span className="text-gray-800">{viewItem.customer.fullName || '—'}</span></div>
              <div><span className="text-gray-600">شماره تلفن:</span> <span className="text-gray-800">{viewItem.customer.phone || '—'}</span></div>
              <div><span className="text-gray-600">شماره تذکره:</span> <span className="text-gray-800">{viewItem.customer.idCardNumber || '—'}</span></div>
              <div className="col-span-2"><span className="text-gray-600">آدرس:</span> <span className="text-gray-800">{viewItem.customer.address || '—'}</span></div>
            </div>
          </div>
        )}

        {/* اطلاعات ضامن */}
        {viewItem.guarantor && viewItem.guarantor.fullName && (
          <div>
            <div className="text-xs text-gray-600 mb-2 flex items-center gap-2 border-r-2 border-orange-500 pr-2">
              <FaUserSecret className="text-orange-600" /> اطلاعات ضامن
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-gray-50 rounded-lg">
              <div><span className="text-gray-600">نام کامل:</span> <span className="text-gray-800">{viewItem.guarantor.fullName}</span></div>
              <div><span className="text-gray-600">شماره تلفن:</span> <span className="text-gray-800">{viewItem.guarantor.phone || '—'}</span></div>
              <div><span className="text-gray-600">نسبت با مشتری:</span> <span className="text-gray-800">{viewItem.guarantor.relationship || '—'}</span></div>
              <div><span className="text-gray-600">شماره تذکره:</span> <span className="text-gray-800">{viewItem.guarantor.idCardNumber || '—'}</span></div>
            </div>
          </div>
        )}

        {/* یادداشت */}
        {viewItem.notes && (
          <div>
            <div className="text-xs text-gray-600 mb-2 flex items-center gap-2 border-r-2 border-yellow-500 pr-2">
              <span>📝</span> توضیحات
            </div>
            <p className="text-gray-600 text-sm bg-gray-50 rounded-xl p-3">{viewItem.notes}</p>
          </div>
        )}
      </div>

      {/* دکمه‌ها */}
      <div className="flex gap-3 pt-4 border-t border-gray-200">
        <button 
          onClick={() => {
            const printContent = `
            <html dir="rtl">
            <head>
              <title>قرارداد کرایه تجهیزات</title>
              <style>
                body {
                  font-family: sans-serif;
                  direction: rtl;
                  padding: 20px;
                  width: 100%;
                  margin: 0 auto;
                  font-size: 12px;
                }
                h2 {
                  text-align: center;
                  font-size: 16px;
                  margin: 0 0 10px 0;
                }
                h3 {
                  font-size: 13px;
                  border-bottom: 1px solid #ccc;
                  padding-bottom: 3px;
                  margin: 15px 0 8px 0;
                }
                .header {
                  text-align: center;
                  border-bottom: 1px solid #333;
                  padding-bottom: 10px;
                  margin-bottom: 15px;
                }
                .logo {
                  font-size: 16px;
                  font-weight: bold;
                }
                .subtitle {
                  font-size: 10px;
                  color: #666;
                }
                .row {
                  display: flex;
                  justify-content: space-between;
                  padding: 4px 0;
                  border-bottom: 1px dotted #eee;
                }
                .label {
                  font-weight: bold;
                  width: 120px;
                }
                .value {
                  flex: 1;
                }
                .signatures {
                  display: flex;
                  justify-content: space-between;
                  margin-top: 50px;
                  padding-top: 15px;
                  border-top: 1px solid #ccc;
                }
                .sign-box {
                  text-align: center;
                  width: 200px;
                }
                .sign-line {
                  margin-top: 40px;
                  border-top: 1px solid #000;
                  padding-top: 5px;
                }
                .footer {
                  text-align: center;
                  margin-top: 30px;
                  padding-top: 10px;
                  border-top: 1px solid #ccc;
                  font-size: 10px;
                  color: #666;
                }
                .section {
                  margin-bottom: 15px;
                }
              </style>
            </head>
            <body>

            <div class="header">
              <div class="logo">ویدا پرودکشن</div>
              <div class="subtitle">VIDA PRODUCTION STUDIO</div>
              <h2>قرارداد کرایه تجهیزات</h2>
            </div>

            <div class="section">
              <h3>اطلاعات تجهیز</h3>
              <div class="row"><span class="label">نام تجهیز:</span><span class="value">${viewItem.name}</span></div>
              <div class="row"><span class="label">دسته بندی:</span><span class="value">${viewItem.category || '—'}</span></div>
              <div class="row"><span class="label">تعداد:</span><span class="value">${viewItem.quantity}</span></div>
              <div class="row"><span class="label">قیمت روزانه:</span><span class="value">${formatCurrency(viewItem.rentalPrice)}</span></div>
              <div class="row"><span class="label">پیش پرداخت:</span><span class="value">${formatCurrency(viewItem.deposit || 0)}</span></div>
              <div class="row"><span class="label">وضعیت فیزیکی:</span><span class="value">${viewItem.condition || 'خوب'}</span></div>
              <div class="row"><span class="label">لوکیشن:</span><span class="value">${viewItem.location || '—'}</span></div>
            </div>

            <div class="section">
              <h3>تاریخ کرایه</h3>
              <div class="row"><span class="label">تاریخ شروع:</span><span class="value">${toShamsi(viewItem.rentalStartDate)}</span></div>
              <div class="row"><span class="label">تاریخ پایان:</span><span class="value">${toShamsi(viewItem.rentalEndDate)}</span></div>
            </div>

            <div class="section">
              <h3>اطلاعات مشتری</h3>
              <div class="row"><span class="label">نام کامل:</span><span class="value">${viewItem.customer?.fullName || '—'}</span></div>
              <div class="row"><span class="label">نام پدر:</span><span class="value">${viewItem.customer?.fatherName || '—'}</span></div>
              <div class="row"><span class="label">شماره تلفن:</span><span class="value">${viewItem.customer?.phone || '—'}</span></div>
              <div class="row"><span class="label">شماره تذکره:</span><span class="value">${viewItem.customer?.idCardNumber || '—'}</span></div>
              <div class="row"><span class="label">آدرس:</span><span class="value">${viewItem.customer?.address || '—'}</span></div>
            </div>

            ${viewItem.guarantor?.fullName ? `
            <div class="section">
              <h3>اطلاعات ضامن</h3>
              <div class="row"><span class="label">نام کامل:</span><span class="value">${viewItem.guarantor.fullName}</span></div>
              <div class="row"><span class="label">شماره تلفن:</span><span class="value">${viewItem.guarantor.phone || '—'}</span></div>
              <div class="row"><span class="label">نسبت با مشتری:</span><span class="value">${viewItem.guarantor.relationship || '—'}</span></div>
              <div class="row"><span class="label">شماره تذکره:</span><span class="value">${viewItem.guarantor.idCardNumber || '—'}</span></div>
            </div>
            ` : ''}

            ${viewItem.notes ? `
            <div class="section">
              <h3>توضیحات</h3>
              <div class="row"><span class="value">${viewItem.notes}</span></div>
            </div>
            ` : ''}

            <div class="signatures">
              <div class="sign-box">
                <div>امضاء مشتری</div>
                <div class="sign-line">........................</div>
              </div>
              <div class="sign-box">
                <div>امضاء شرکت</div>
                <div class="sign-line">........................</div>
              </div>
            </div>

            <div class="footer">
              <p>تاریخ چاپ: ${toShamsi(new Date())}</p>
              <p>این قرارداد توسط سیستم مدیریت ویدا پرودکشن صادر شده است</p>
            </div>

            </body>
            </html>
            `;
            const win = window.open('', '_blank');
            win.document.write(printContent);
            win.document.close();
            win.print();
            win.close();
          }}
          className="flex-1 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2"
        >
          <MdPrint size={16} /> چاپ قرارداد
        </button>
        <button 
          onClick={() => setViewItem(null)} 
          className="flex-1 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-500 transition-colors"
        >
          بستن
        </button>
      </div>
    </div>
  </Modal>
)}

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="حذف تجهیز" message="آیا مطمئن هستید؟" />
    </div>
  );
}